import { sql } from "drizzle-orm";
import { pgTable, text, varchar, integer, boolean, timestamp, jsonb, index } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import type { z } from "zod";

import { tenants } from './common';

export const notifications = pgTable("notifications", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  type: text("type").notNull(),
  category: text("category"),
  severity: text("severity").default("info"),
  priority: text("priority").default("normal"),
  title: text("title").notNull(),
  message: text("message").notNull(),
  templateId: text("template_id"),
  sourceModule: text("source_module"),
  relatedPermitId: text("related_permit_id"),
  relatedEntityType: text("related_entity_type"),
  relatedEntityId: text("related_entity_id"),
  targetRole: text("target_role"),
  targetUserId: text("target_user_id"),
  targetTeam: text("target_team"),
  targetCompany: text("target_company"),
  targetCompanyId: text("target_company_id"),
  isRead: boolean("is_read").notNull().default(false),
  readAt: text("read_at"),
  isAcknowledged: boolean("is_acknowledged").notNull().default(false),
  acknowledgedAt: text("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
  isClicked: boolean("is_clicked").notNull().default(false),
  clickedAt: text("clicked_at"),
  deliveryChannels: jsonb("delivery_channels").$type<string[]>().default([]),
  deliveredChannels: jsonb("delivered_channels").$type<string[]>().default([]),
  deliveryState: text("delivery_state").default("pending"),
  deepLink: text("deep_link"),
  actionButtons: jsonb("action_buttons").$type<{ label: string; action: string; variant?: string }[]>().default([]),
  digestEligible: boolean("digest_eligible").notNull().default(false),
  digestGroup: text("digest_group"),
  deduplicationKey: text("deduplication_key"),
  suppressedAt: text("suppressed_at"),
  scheduledFor: text("scheduled_for"),
  expiresAt: text("expires_at"),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxNotificationsTargetUserCreated: index("idx_notifications_target_user_created").on(t.targetUserId, t.createdAt),
  idxNotificationsTargetUserIsRead: index("idx_notifications_target_user_is_read").on(t.targetUserId, t.isRead),
}));
export const insertNotificationSchema = createInsertSchema(notifications).omit({ id: true, tenantId: true });
export type InsertNotification = z.infer<typeof insertNotificationSchema>;
export type Notification = typeof notifications.$inferSelect;

// ─── Web Push subscriptions (PTW-AUD-522, Task #3600) ────────────────
// A single user/device registers a browser PushSubscription so the platform
// can deliver real Web Push notifications (VAPID). One row per unique
// endpoint; the endpoint is the natural key the push service issues. The
// p256dh + auth keys are the subscription's public encryption material (not
// secrets) supplied by the browser PushManager.
export const pushSubscriptions = pgTable("push_subscriptions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  userId: text("user_id").notNull(),
  endpoint: text("endpoint").notNull().unique(),
  p256dh: text("p256dh").notNull(),
  auth: text("auth").notNull(),
  userAgent: text("user_agent"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow().notNull(),
  lastUsedAt: timestamp("last_used_at", { withTimezone: true }),
}, (t) => ({
  idxPushSubscriptionsUser: index("idx_push_subscriptions_user").on(t.userId),
  idxPushSubscriptionsTenant: index("idx_push_subscriptions_tenant").on(t.tenantId),
}));
export const insertPushSubscriptionSchema = createInsertSchema(pushSubscriptions).omit({ id: true, createdAt: true, lastUsedAt: true });
export type InsertPushSubscription = z.infer<typeof insertPushSubscriptionSchema>;
export type PushSubscription = typeof pushSubscriptions.$inferSelect;

// ─── Action Platform: Alerts ─────────────────────────────────────────
export const alerts = pgTable("alerts", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  title: text("title").notNull(),
  description: text("description"),
  type: text("type").notNull(),
  category: text("category"),
  severity: text("severity").notNull().default("medium"),
  priority: text("priority").notNull().default("normal"),
  status: text("status").notNull().default("open"),
  sourceModule: text("source_module"),
  sourceEntityType: text("source_entity_type"),
  sourceEntityId: text("source_entity_id"),
  assignedToUser: text("assigned_to_user"),
  assignedToRole: text("assigned_to_role"),
  assignedToTeam: text("assigned_to_team"),
  ownedByCompany: text("owned_by_company"),
  linkedTaskId: text("linked_task_id"),
  escalationPolicyId: text("escalation_policy_id"),
  slaAcknowledgeDue: text("sla_acknowledge_due"),
  slaResponseDue: text("sla_response_due"),
  slaResolutionDue: text("sla_resolution_due"),
  acknowledgedAt: text("acknowledged_at"),
  acknowledgedBy: text("acknowledged_by"),
  resolvedAt: text("resolved_at"),
  resolvedBy: text("resolved_by"),
  closedAt: text("closed_at"),
  closedBy: text("closed_by"),
  notes: text("notes"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertAlertSchema = createInsertSchema(alerts).omit({ id: true, tenantId: true });
export type InsertAlert = z.infer<typeof insertAlertSchema>;
export type Alert = typeof alerts.$inferSelect;

// ─── Action Platform: Escalation Policies ────────────────────────────
export const escalationPolicies = pgTable("escalation_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  stages: jsonb("stages").$type<{
    stageNumber: number;
    delayMinutes: number;
    targetRoles: string[];
    targetUsers?: string[];
    notificationChannels?: string[];
  }[]>().notNull().default([]),
  fallbackRoute: text("fallback_route"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertEscalationPolicySchema = createInsertSchema(escalationPolicies).omit({ id: true, tenantId: true });
export type InsertEscalationPolicy = z.infer<typeof insertEscalationPolicySchema>;
export type EscalationPolicy = typeof escalationPolicies.$inferSelect;

// ─── Action Platform: Escalations ────────────────────────────────────
export const escalations = pgTable("escalations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  reason: text("reason").notNull(),
  reasonCategory: text("reason_category"),
  sourceEntityType: text("source_entity_type").notNull(),
  sourceEntityId: text("source_entity_id").notNull(),
  escalationPolicyId: text("escalation_policy_id"),
  currentStage: integer("current_stage").notNull().default(1),
  totalStages: integer("total_stages"),
  fromUser: text("from_user"),
  fromRole: text("from_role"),
  toUser: text("to_user"),
  toRole: text("to_role"),
  toTeam: text("to_team"),
  status: text("status").notNull().default("active"),
  escalatedAt: text("escalated_at").notNull(),
  acknowledgedAt: text("acknowledged_at"),
  resolvedAt: text("resolved_at"),
  notes: text("notes"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().default({}),
});
export const insertEscalationSchema = createInsertSchema(escalations).omit({ id: true, tenantId: true });
export type InsertEscalation = z.infer<typeof insertEscalationSchema>;
export type Escalation = typeof escalations.$inferSelect;

// ─── Action Platform: Platform Tasks ─────────────────────────────────
export const platformTasks = pgTable("platform_tasks", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  title: text("title").notNull(),
  description: text("description"),
  type: text("type").notNull(),
  category: text("category"),
  priority: text("priority").notNull().default("normal"),
  scope: text("scope"),
  status: text("status").notNull().default("open"),
  assignedToUser: text("assigned_to_user"),
  assignedToRole: text("assigned_to_role"),
  assignedToTeam: text("assigned_to_team"),
  ownedByCompany: text("owned_by_company"),
  dueDate: text("due_date"),
  slaTargetDate: text("sla_target_date"),
  slaBreached: boolean("sla_breached").notNull().default(false),
  slaBreachedAt: text("sla_breached_at"),
  reminderSchedule: jsonb("reminder_schedule").$type<string[]>().default([]),
  requiresEvidence: boolean("requires_evidence").notNull().default(false),
  requiresApproval: boolean("requires_approval").notNull().default(false),
  requiresVerification: boolean("requires_verification").notNull().default(false),
  evidenceAttached: boolean("evidence_attached").notNull().default(false),
  approvedBy: text("approved_by"),
  approvedAt: text("approved_at"),
  verifiedBy: text("verified_by"),
  verifiedAt: text("verified_at"),
  parentTaskId: text("parent_task_id"),
  dependencies: jsonb("dependencies").$type<string[]>().default([]),
  sourceEntityType: text("source_entity_type"),
  sourceEntityId: text("source_entity_id"),
  completedAt: text("completed_at"),
  completedBy: text("completed_by"),
  notes: text("notes"),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertPlatformTaskSchema = createInsertSchema(platformTasks).omit({ id: true, tenantId: true });
export type InsertPlatformTask = z.infer<typeof insertPlatformTaskSchema>;
export type PlatformTask = typeof platformTasks.$inferSelect;

// ─── Action Platform: Inboxes ────────────────────────────────────────
export const inboxes = pgTable("inboxes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  type: text("type").notNull().default("personal"),
  ownerUserId: text("owner_user_id"),
  ownerRole: text("owner_role"),
  ownerTeam: text("owner_team"),
  ownerCompany: text("owner_company"),
  isShared: boolean("is_shared").notNull().default(false),
  routingRules: jsonb("routing_rules").$type<Record<string, unknown>>().default({}),
  itemTypeFilters: jsonb("item_type_filters").$type<string[]>().default([]),
  priorityOrdering: text("priority_ordering").default("priority"),
  slaDisplayRules: jsonb("sla_display_rules").$type<Record<string, unknown>>().default({}),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertInboxSchema = createInsertSchema(inboxes).omit({ id: true, tenantId: true });
export type InsertInbox = z.infer<typeof insertInboxSchema>;
export type Inbox = typeof inboxes.$inferSelect;

// ─── Action Platform: Inbox Items ────────────────────────────────────
export const inboxItems = pgTable("inbox_items", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  inboxId: text("inbox_id").notNull(),
  itemType: text("item_type").notNull(),
  itemId: text("item_id").notNull(),
  status: text("status").notNull().default("unread"),
  priority: text("priority").default("normal"),
  claimedBy: text("claimed_by"),
  claimedAt: text("claimed_at"),
  assignedTo: text("assigned_to"),
  assignedAt: text("assigned_at"),
  snoozedUntil: text("snoozed_until"),
  archivedAt: text("archived_at"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
}, (t) => ({
  idxInboxItemsInboxCreatedAt: index("idx_inbox_items_inbox_created_at").on(t.inboxId, t.createdAt),
}));
export const insertInboxItemSchema = createInsertSchema(inboxItems).omit({ id: true, tenantId: true });
export type InsertInboxItem = z.infer<typeof insertInboxItemSchema>;
export type InboxItem = typeof inboxItems.$inferSelect;

// ─── Action Platform: Orchestration Rules ────────────────────────────
export const orchestrationRules = pgTable("orchestration_rules", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  status: text("status").notNull().default("draft"),
  version: integer("version").notNull().default(1),
  priority: integer("priority").notNull().default(100),
  triggerEventType: text("trigger_event_type").notNull(),
  triggerModule: text("trigger_module"),
  conditions: jsonb("conditions").$type<Record<string, any>[]>().default([]),
  actions: jsonb("actions").$type<{
    type: string;
    config: Record<string, any>;
  }[]>().notNull().default([]),
  fanOutTargets: jsonb("fan_out_targets").$type<{
    targetType: string;
    targetId?: string;
    targetRole?: string;
    targetTeam?: string;
  }[]>().default([]),
  deduplicationKey: text("deduplication_key"),
  deduplicationWindowMinutes: integer("deduplication_window_minutes"),
  suppressionEventType: text("suppression_event_type"),
  suppressionConditions: jsonb("suppression_conditions").$type<Record<string, any>[]>().default([]),
  delayMinutes: integer("delay_minutes"),
  scheduleExpression: text("schedule_expression"),
  chainedRuleIds: jsonb("chained_rule_ids").$type<string[]>().default([]),
  enabled: boolean("enabled").notNull().default(true),
  tags: jsonb("tags").$type<string[]>().default([]),
  createdBy: text("created_by"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
  publishedAt: text("published_at"),
  archivedAt: text("archived_at"),
});
export const insertOrchestrationRuleSchema = createInsertSchema(orchestrationRules).omit({ id: true, tenantId: true });
export type InsertOrchestrationRule = z.infer<typeof insertOrchestrationRuleSchema>;
export type OrchestrationRule = typeof orchestrationRules.$inferSelect;

// ─── Action Platform: Orchestration Execution Log ────────────────────
export const orchestrationExecutionLog = pgTable("orchestration_execution_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  ruleId: text("rule_id").notNull(),
  ruleName: text("rule_name").notNull(),
  eventType: text("event_type").notNull(),
  eventData: jsonb("event_data").$type<Record<string, any>>().default({}),
  matchResult: text("match_result").notNull(),
  actionsExecuted: jsonb("actions_executed").$type<{
    actionType: string;
    success: boolean;
    result?: any;
    error?: string;
  }[]>().default([]),
  deduplicated: boolean("deduplicated").notNull().default(false),
  suppressed: boolean("suppressed").notNull().default(false),
  delayed: boolean("delayed").notNull().default(false),
  scheduledFor: text("scheduled_for"),
  executedAt: text("executed_at"),
  executionDurationMs: integer("execution_duration_ms"),
  chainedFromRuleId: text("chained_from_rule_id"),
  chainedFromLogId: text("chained_from_log_id"),
  error: text("error"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertOrchestrationExecutionLogSchema = createInsertSchema(orchestrationExecutionLog).omit({ id: true, tenantId: true });
export type InsertOrchestrationExecutionLog = z.infer<typeof insertOrchestrationExecutionLogSchema>;
export type OrchestrationExecutionLog = typeof orchestrationExecutionLog.$inferSelect;

// ─── Action Platform: Orchestration Delayed Actions ──────────────────
export const orchestrationDelayedActions = pgTable("orchestration_delayed_actions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  ruleId: text("rule_id").notNull(),
  eventType: text("event_type").notNull(),
  eventData: jsonb("event_data").$type<Record<string, any>>().default({}),
  actions: jsonb("actions").$type<{
    type: string;
    config: Record<string, any>;
  }[]>().notNull().default([]),
  scheduledFor: text("scheduled_for").notNull(),
  status: text("status").notNull().default("pending"),
  executedAt: text("executed_at"),
  cancelledAt: text("cancelled_at"),
  cancellationReason: text("cancellation_reason"),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
});
export const insertOrchestrationDelayedActionSchema = createInsertSchema(orchestrationDelayedActions).omit({ id: true, tenantId: true });
export type InsertOrchestrationDelayedAction = z.infer<typeof insertOrchestrationDelayedActionSchema>;
export type OrchestrationDelayedAction = typeof orchestrationDelayedActions.$inferSelect;

// ─── Action Platform: SLA Policies ───────────────────────────────────
export const slaPolicies = pgTable("sla_policies", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  description: text("description"),
  severityBand: text("severity_band").notNull(),
  acknowledgeTargetMinutes: integer("acknowledge_target_minutes"),
  responseTargetMinutes: integer("response_target_minutes"),
  completionTargetMinutes: integer("completion_target_minutes"),
  reminderCadenceMinutes: integer("reminder_cadence_minutes"),
  escalationAfterMinutes: integer("escalation_after_minutes"),
  useWorkingHours: boolean("use_working_hours").notNull().default(true),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertSlaPolicySchema = createInsertSchema(slaPolicies).omit({ id: true, tenantId: true });
export type InsertSlaPolicy = z.infer<typeof insertSlaPolicySchema>;
export type SlaPolicy = typeof slaPolicies.$inferSelect;

// ─── Action Platform: Notification Templates ─────────────────────────
export const notificationTemplates = pgTable("notification_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  name: text("name").notNull(),
  subject: text("subject"),
  body: text("body").notNull(),
  placeholders: jsonb("placeholders").$type<string[]>().default([]),
  severityStyling: jsonb("severity_styling").$type<Record<string, string>>().default({}),
  ctaButtons: jsonb("cta_buttons").$type<{ label: string; action: string; variant?: string }[]>().default([]),
  channelVariants: jsonb("channel_variants").$type<Record<string, { subject?: string; body: string }>>().default({}),
  deepLinkTemplate: text("deep_link_template"),
  category: text("category"),
  isActive: boolean("is_active").notNull().default(true),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertNotificationTemplateSchema = createInsertSchema(notificationTemplates).omit({ id: true, tenantId: true });
export type InsertNotificationTemplate = z.infer<typeof insertNotificationTemplateSchema>;
export type NotificationTemplate = typeof notificationTemplates.$inferSelect;

// ─── Action Platform: Notification Preferences ───────────────────────
export const notificationPreferences = pgTable("notification_preferences", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  userId: text("user_id"),
  role: text("role"),
  channels: jsonb("channels").$type<string[]>().default([]),
  deliveryMode: text("delivery_mode").default("immediate"),
  digestFrequency: text("digest_frequency"),
  quietHoursStart: text("quiet_hours_start"),
  quietHoursEnd: text("quiet_hours_end"),
  quietHoursTimezone: text("quiet_hours_timezone"),
  mandatoryOverride: boolean("mandatory_override").notNull().default(false),
  categoryOverrides: jsonb("category_overrides").$type<Record<string, { channels?: string[]; deliveryMode?: string }>>().default({}),
  createdAt: timestamp("created_at", { withTimezone: true }).defaultNow(),
  updatedAt: timestamp("updated_at", { withTimezone: true }).defaultNow(),
});
export const insertNotificationPreferenceSchema = createInsertSchema(notificationPreferences).omit({ id: true, tenantId: true });
export type InsertNotificationPreference = z.infer<typeof insertNotificationPreferenceSchema>;
export type NotificationPreference = typeof notificationPreferences.$inferSelect;

// ─── Action Platform: Audit Events ───────────────────────────────────
export const actionAuditEvents = pgTable("action_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  action: text("action").notNull(),
  performedBy: text("performed_by"),
  performedByRole: text("performed_by_role"),
  previousState: jsonb("previous_state").$type<Record<string, unknown>>(),
  newState: jsonb("new_state").$type<Record<string, unknown>>(),
  metadata: jsonb("metadata").$type<Record<string, unknown>>().default({}),
  ipAddress: text("ip_address"),
  timestamp: text("timestamp").notNull(),
});
export const insertActionAuditEventSchema = createInsertSchema(actionAuditEvents).omit({ id: true, tenantId: true });
export type InsertActionAuditEvent = z.infer<typeof insertActionAuditEventSchema>;
export type ActionAuditEvent = typeof actionAuditEvents.$inferSelect;

// ─── Projects ────────────────────────────────────────────────────────
export const notificationAuditEvents = pgTable("notification_audit_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  tenantId: text("tenant_id").notNull().references(() => tenants.id),
  entityType: text("entity_type").notNull(),
  entityId: text("entity_id").notNull(),
  eventType: text("event_type").notNull(),
  actor: text("actor").notNull(),
  actorRole: text("actor_role"),
  timestamp: text("timestamp").notNull(),
  oldValues: jsonb("old_values").$type<Record<string, any>>(),
  newValues: jsonb("new_values").$type<Record<string, any>>(),
  metadata: jsonb("metadata").$type<Record<string, any>>().default({}),
});
export const insertNotificationAuditEventSchema = createInsertSchema(notificationAuditEvents).omit({ id: true, tenantId: true });
export type InsertNotificationAuditEvent = z.infer<typeof insertNotificationAuditEventSchema>;
export type NotificationAuditEvent = typeof notificationAuditEvents.$inferSelect;

// ─── Visitors ────────────────────────────────────────────────────────
// Note: visitors.company is intentionally a free-text field (not FK) because
// visitors are external people whose companies may not exist in the system.
